import { useEffect, useReducer, useRef, useState } from 'react';
import DropDown from 'components/NewLayout/Dropdown';
import { postAttachments, updatePaymentSchedule, updatePurchaseOrder } from 'api/index';
import type { BillUpdateForm, FileData, GetPurchaseOrders } from 'api/types';
import Validator from 'simple-react-validator';
import { getS3Path, getValidations } from 'utils/index';
import { useCommonStore, usePaymentWorkspaceStore } from 'store/index';
import CustomDialog from 'components/NewLayout/Dialog';
import CommentsIcon from 'assets/images/comments-icon.svg';
import CustomButton from 'components/NewLayout/Button';
import { Typography } from '@mui/material';
import InfoIcon from 'assets/images/info-icon.svg';
import toast from 'react-hot-toast';
import FileUploadModal from 'components/FileUploadModal';
import UploadImage from 'components/UploadImage';
import { paymentStatus } from 'utils/constants';
interface Bill {
  bill_id: number;
  purchase_order_id: string;
  status: string;
}
export default function BillUpdateModal({
  selectedBill,
  onClose,
  onComplete,
  selectedPurchaseOrder,
  setSelectedPurchaseOrder
}: {
  selectedBill: Bill;
  onClose: () => void;
  onComplete?: () => void;
  selectedPurchaseOrder?: GetPurchaseOrders;
  setSelectedPurchaseOrder?: (purchaseOrder: GetPurchaseOrders) => void;
}) {
  const [modalShow, setShowModal] = useState<boolean>(true);
  const [isWarningModalVisible, setWarningModalVisibility] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [data, setData] = useState<BillUpdateForm>({
    status: '',
    bill_id: selectedBill.bill_id,
    purchase_order_id: selectedPurchaseOrder?.purchase_order_id
  });

  const [showAddAttachmentModal, setAddAttachmentModalVisibility] = useState<boolean>(false);

  const validator = useRef(new Validator(getValidations()));
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const updatePaymentScheduleItem = usePaymentWorkspaceStore(
    (state) => state.updatePaymentScheduleItem
  );
  const setSnackBarData = useCommonStore((state) => state.setSnackBarData);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setData({ ...data, [name]: value });
  };

  useEffect(() => {
    if (selectedBill) {
      setShowModal(true);
    }
  }, []);

  const handleUpdateButtonClick = () => {
    if (selectedBill?.status === 'Needs Allocation') {
      setWarningModalVisibility(true);
      setShowModal(false);
    } else if (data?.status === 'Paid' && !selectedPurchaseOrder?.attachments?.length) {
      setAddAttachmentModalVisibility(true);
      setShowModal(false);
    } else {
      submitForm();
    }
  };

  const onConfirmAttachment = async (fileData: FileData) => {
    if (!fileData.file || !selectedPurchaseOrder) {
      setAddAttachmentModalVisibility(false);
      return;
    }

    setLoader(true);

    try {
      const fileName = `${Date.now()}_${fileData.fileName.replaceAll(' ', '-')}`;
      const myNewFile = new File([fileData.file], fileName, {
        type: fileData.file.type
      });
      const imagePath = getS3Path(fileName, 'purchase-order');

      await UploadImage(myNewFile, imagePath);

      await postAttachments(
        [imagePath],
        'PURCHASE_ORDER',
        selectedPurchaseOrder.purchase_order_id?.toString()
      );

      const dataToUpdate: GetPurchaseOrders = {
        purchase_order_id: selectedPurchaseOrder.purchase_order_id,
        attachmentPaths: [imagePath]
      };

      await updatePurchaseOrder(dataToUpdate, selectedPurchaseOrder.purchase_order_id);

      setSelectedPurchaseOrder &&
        setSelectedPurchaseOrder({
          ...selectedPurchaseOrder,
          attachments: [
            {
              id: Date.now(), // temporary ID until refetch
              entity: 'PURCHASE_ORDER',
              entity_id: selectedPurchaseOrder.purchase_order_id,
              uri: imagePath,
              created_at: new Date().toISOString(),
              created_by: localStorage.getItem('userId') || ''
            }
          ]
        });

      setShowModal(true);

      toast.success('Attachment uploaded successfully');
    } catch (error) {
      console.error('Error uploading attachment:', error);
      toast.error('Error uploading attachment. Please try again.', {
        position: 'top-center',
        id: 'upload_error'
      });
      onClose();
    } finally {
      setLoader(false);
      setAddAttachmentModalVisibility(false);
    }
  };

  const submitForm = () => {
    if (validator.current.allValid()) {
      if (!data?.bill_id) {
        console.error('Bill ID is required but not provided.');
        return;
      }
      setLoader(true);
      updatePaymentSchedule(data, data?.bill_id)
        .then((res: { message: string }) => {
          if (res.message === 'Payment Schedule updated successfully!') {
            updatePaymentScheduleItem(data);
            setLoader(false);
            setSnackBarData({
              type: 'success',
              message: 'Successfully updated Bill Status',
              open: true
            });
            if (onComplete) onComplete();
          }
        })
        .catch((err) => {
          console.error(err);
          if (err.response?.data?.error?.includes('Main project has no QuickBooks ID')) {
            toast.error(
              'Bill failed due to invalid job or customer reference. Please reach out to a oneXerp Administrator and give them the PO Number to get this resolved.',
              {
                position: 'top-center',
                id: 'no_quickbook_id'
              }
            );
          }
        })
        .finally(() => {
          onClose();
          if (onComplete) onComplete();
          setShowModal(false);
          setLoader(false);
          onClose();
        });
    } else {
      validator.current.showMessages();
      forceUpdate();
    }
  };

  return (
    <>
      <CustomDialog
        title={
          <Typography component="div" sx={{ color: '#335d87' }}>
            <img src={CommentsIcon} width="30px" /> Update Bill Status
          </Typography>
        }
        open={modalShow}
        maxWidth="xs"
        closeDialog={() => onClose()}
        loading={loader}
        content={
          <DropDown
            label="Bill Status"
            inputName="status"
            optionText="Select payment method"
            value={data.status}
            onChange={(event) => handleChange(event)}
            options={paymentStatus}
            required
            validator={validator}
          />
        }
        actions={
          <>
            <CustomButton onClick={() => onClose()} variant="outlined" label="Discard" />
            <CustomButton onClick={() => handleUpdateButtonClick()} label="Update Bill" />
          </>
        }
      />

      <CustomDialog
        title={
          <Typography component="div" sx={{ color: '#335d87' }}>
            <img src={InfoIcon} width="30px" />
            Warning
          </Typography>
        }
        open={isWarningModalVisible}
        maxWidth="xs"
        closeDialog={() => {
          setWarningModalVisibility(false);
          setShowModal(true);
        }}
        loading={loader}
        content={
          <Typography>
            This payment schedule was created by the system. The due date is still set to
            01-01-2099. Are you sure you want to proceed with changing this status?
          </Typography>
        }
        actions={
          <>
            <CustomButton onClick={() => onClose()} variant="outlined" label="Discard" />
            <CustomButton onClick={submitForm} label="Update Bill" />
          </>
        }
      />

      {showAddAttachmentModal && (
        <FileUploadModal
          open={showAddAttachmentModal}
          onClose={() => {
            setAddAttachmentModalVisibility(false);
            onClose();
          }}
          onConfirm={(fileData) => onConfirmAttachment(fileData)}
          loading={loader}
        />
      )}
    </>
  );
}
